import { useEffect, useState, type FormEvent } from "react";
import {
  ArrowDown,
  ArrowRight,
  ArrowUpRight,
  Check,
  Factory,
  Mail,
  MessageCircle,
  Phone,
  Play,
  QrCode,
  ScanLine,
  Store,
  Users,
} from "lucide-react";
import { Link } from "react-router";
import { useLanguage } from "../../i18n/language";
import { translations, type LandingLanguage } from "./RewardsLandingPage";
import { ScanDemoScreenshot } from "./RewardsDemoVisuals";
import { rewardsSiteCopy } from "./RewardsSiteCopy";
import { RewardsSiteFooter, RewardsSiteHeader } from "./RewardsSiteChrome";

const ASSET_ROOT = "/marketing/gatiq-rewards";
const featureImages = [
  "zenith-admin-overview.png",
  "zenith-reward-batches.png",
  "zenith-performance-tracker.png",
  "zenith-document-upload.png",
  "zenith-karigar-documents.png",
];
const platformScreenInfo: Record<LandingLanguage, Array<{ title: string; alt: string; description: string }>> = {
  en: [
    { title: "Admin overview", alt: "Zenith Pro Adhesives Admin overview dashboard", description: "Review network activity and approvals from the Admin workspace." },
    { title: "Reward code batches", alt: "Zenith Pro Adhesives reward code batch screen", description: "Follow issued reward batches and their scan status." },
    { title: "Performance tracker", alt: "Zenith Pro Adhesives performance tracker", description: "Compare product and counter activity across the selected period." },
    { title: "Share product information", alt: "Zenith Pro Adhesives document upload screen", description: "Publish product information as a PDF, image, or link." },
    { title: "Karigar product documents", alt: "Zenith Club Karigar app product documents list", description: "Karigars can open the product information shared by their company." },
  ],
  hi: [
    { title: "Admin डैशबोर्ड", alt: "Zenith Pro Adhesives Admin overview dashboard", description: "Admin workspace में नेटवर्क गतिविधि और अनुमोदन देखें।" },
    { title: "रिवॉर्ड कोड बैच", alt: "Zenith Pro Adhesives reward code batch screen", description: "जारी किए गए रिवॉर्ड बैच और उनके स्कैन की स्थिति देखें।" },
    { title: "परफॉर्मेंस ट्रैकर", alt: "Zenith Pro Adhesives performance tracker", description: "चुनी हुई अवधि में प्रोडक्ट और काउंटर गतिविधि की तुलना करें।" },
    { title: "प्रोडक्ट जानकारी साझा करें", alt: "Zenith Pro Adhesives document upload screen", description: "प्रोडक्ट जानकारी PDF, इमेज या लिंक के रूप में प्रकाशित करें।" },
    { title: "कारीगर के प्रोडक्ट दस्तावेज़", alt: "Zenith Club Karigar app product documents list", description: "कारीगर कंपनी की साझा की गई प्रोडक्ट जानकारी खोल सकते हैं।" },
  ],
};
const roleIcons = [Factory, Users, Store, Users];
const journeyIcons = [QrCode, Users, ScanLine, Check];

type DemoScreen = { image: string; title: string; alt: string; phone?: boolean; scan?: boolean };

function DemoScreensShowcase({
  lang,
  eyebrow,
  title,
  description,
  screens,
  connected = false,
}: {
  lang: LandingLanguage;
  eyebrow: string;
  title: string;
  description: string;
  screens: DemoScreen[];
  connected?: boolean;
}) {
  return (
    <section className="gq-lp-section gq-lp-subpage-showcase">
      <div className="gq-lp-wrap">
        <div className="gq-lp-section-intro is-centered">
          <p className="gq-lp-eyebrow is-orange">{eyebrow}</p>
          <h2>{title}</h2>
          <p>{description}</p>
        </div>
        <div className="gq-lp-subpage-screen-grid">
          {screens.map((screen) => (
            <figure className={`gq-lp-subpage-screen${screen.phone ? " is-phone" : ""}`} key={screen.image}>
              {screen.scan ? <ScanDemoScreenshot alt={screen.alt} /> : <img src={`${ASSET_ROOT}/${screen.image}`} alt={screen.alt} loading="lazy" />}
              <figcaption>{screen.title}</figcaption>
            </figure>
          ))}
        </div>
        {connected ? (
          <div className="gq-lp-role-linkage">
            <svg viewBox="0 0 1000 155" role="img" aria-label={lang === "en" ? "Admin, Counter, and Karigar connect through GatiQ Rewards" : "Admin, काउंटर और कारीगर GatiQ Rewards से जुड़े हैं"}>
              <defs><marker id="gq-lp-subpage-link-arrow" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto"><path d="M0 0 L8 4 L0 8 Z" fill="#c54e1b" /></marker></defs>
              <path className="gq-lp-link-path" d="M165 0 C165 58 360 74 500 118" markerEnd="url(#gq-lp-subpage-link-arrow)" />
              <path className="gq-lp-link-path" d="M500 0 L500 118" markerEnd="url(#gq-lp-subpage-link-arrow)" />
              <path className="gq-lp-link-path" d="M835 0 C835 58 640 74 500 118" markerEnd="url(#gq-lp-subpage-link-arrow)" />
            </svg>
            <div className="gq-lp-role-hub"><img src={`${ASSET_ROOT}/gatiq-logo-transparent.png`} alt="" /><b>GatiQ Rewards</b><span>{lang === "en" ? "One connected platform" : "एक जुड़ा हुआ प्लेटफ़ॉर्म"}</span></div>
          </div>
        ) : null}
        <p className="gq-lp-demo-disclosure">{lang === "en" ? "Screenshots are from the Zenith Pro Adhesives demo workspace." : "स्क्रीनशॉट Zenith Pro Adhesives डेमो वर्कस्पेस के हैं।"}</p>
      </div>
    </section>
  );
}

export type RewardsSubpageKey = "karigars" | "companies" | "help" | "journey" | "platform" | "film" | "privacy" | "terms" | "contact";
type LandingCopy = (typeof translations)[LandingLanguage];
type SiteCopy = (typeof rewardsSiteCopy)[keyof typeof rewardsSiteCopy];

export function RewardsSiteSubpage({ page }: { page: RewardsSubpageKey }) {
  const { lang } = useLanguage("en");
  const t = translations[lang];
  const copy = rewardsSiteCopy[lang];
  const pageCopy = copy.pages[page];
  const [activeFeature, setActiveFeature] = useState(0);

  useEffect(() => {
    document.title = `${pageCopy.title} | GatiQ Rewards`;
  }, [pageCopy.title]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [page]);

  return (
    <div className="gq-landing gq-lp-multipage" data-scrollable-page data-language={lang}>
      <a className="gq-lp-skip" href="#gq-page-main">{t.skip}</a>
      <RewardsSiteHeader activePath={`/${page === "journey" ? "how-it-works" : page === "film" ? "product-film" : page === "karigars" ? "for-karigars" : page === "companies" ? "for-companies" : page}`} />
      <main id="gq-page-main">
        <section className="gq-lp-subpage-hero">
          <div className="gq-lp-wrap">
            <p className="gq-lp-eyebrow"><span />{pageCopy.eyebrow}</p>
            <h1>{pageCopy.title}</h1>
            <p>{pageCopy.description}</p>
            {"updated" in pageCopy ? <small>{pageCopy.updated}</small> : null}
          </div>
        </section>

        {page === "karigars" ? <KarigarsContent t={t} lang={lang} /> : null}
        {page === "companies" ? <CompaniesContent t={t} lang={lang} activeFeature={activeFeature} setActiveFeature={setActiveFeature} /> : null}
        {page === "help" ? <HelpContent t={t} lang={lang} /> : null}
        {page === "journey" ? <JourneyContent t={t} lang={lang} /> : null}
        {page === "platform" ? (
          <PlatformContent
            t={t}
            lang={lang}
            activeFeature={activeFeature}
            setActiveFeature={setActiveFeature}
          />
        ) : null}
        {page === "film" ? <FilmContent t={t} /> : null}
        {page === "privacy" ? <LegalContent sections={copy.privacy.sections} /> : null}
        {page === "terms" ? <LegalContent sections={copy.terms.sections} /> : null}
        {page === "contact" ? <ContactContent copy={copy.contact} privacyLabel={copy.footer.privacy} /> : null}
        {page === "contact" ? (
          <DemoScreensShowcase
            lang={lang}
            eyebrow={lang === "en" ? "THE CONNECTED REWARD NETWORK" : "जुड़ा हुआ रिवॉर्ड नेटवर्क"}
            title={lang === "en" ? "One platform for every role" : "हर भूमिका के लिए एक प्लेटफ़ॉर्म"}
            description={lang === "en" ? "Zenith Pro Adhesives uses the Admin dashboard, Counter workspace, and Karigar app in one reward journey." : "Zenith Pro Adhesives Admin डैशबोर्ड, काउंटर वर्कस्पेस और कारीगर ऐप को एक रिवॉर्ड यात्रा में जोड़ता है।"}
            screens={[
              { image: "zenith-admin-overview.png", title: lang === "en" ? "Admin" : "Admin", alt: "Zenith Pro Adhesives Admin dashboard" },
              { image: "zenith-counter-dashboard.png", title: lang === "en" ? "Counter" : "काउंटर", alt: "Zenith Pro Adhesives Counter dashboard" },
              { image: "zenith-karigar-home.png", title: lang === "en" ? "Karigar · Mobile app" : "कारीगर · मोबाइल ऐप", alt: "Zenith Club Karigar mobile app home", phone: true },
            ]}
            connected
          />
        ) : null}
      </main>
      <RewardsSiteFooter />
    </div>
  );
}

const audienceCopy = {
  en: {
    karigars: {
      intro: "A reward journey in simple steps",
      introText: "You do the skilled work. GatiQ Rewards helps you see when an eligible product has a reward code and what to do next.",
      steps: [
        { title: "Find the code", description: "Look for a GatiQ Rewards QR code on a product included in your company’s programme." },
        { title: "Scan or enter it", description: "Use the app to scan the QR code. If scanning is difficult, enter the number printed below it." },
        { title: "See your QCoin", description: "When a code is valid, the QCoin entry appears in your app. You can also check your balance and activity." },
        { title: "Follow the claim steps", description: "After your registration is approved, send a claim request in the app and follow the programme’s instructions." },
      ],
      noteTitle: "Every company’s programme can be different",
      note: "The company decides which products qualify, how many QCoin a code adds, and which rewards are available. Ask your company representative or counter about your programme.",
      screenTitle: "Your reward journey, in the app",
      screenAlt: "GatiQ Rewards mobile app screen showing the karigar reward flow",
      help: "Need a hand? Visit Help",
      contact: "Talk to our team",
    },
    companies: {
      intro: "Tools to run and follow your programme",
      introText: "Manage verified reward workflows across your team with a shared view of code batches, network activity, and product information.",
      capabilities: [
        { title: "Issue code batches", description: "Create reward code batches for products in your programme." },
        { title: "Connect your network", description: "Bring distributors, counters, and karigars into the programme flow." },
        { title: "Follow scans and batches", description: "Review batch records from issue through scan activity." },
        { title: "Review and share", description: "See product and counter performance, and share product information with your network." },
      ],
      noteTitle: "Your programme rules stay in your hands",
      note: "Each company defines its eligible products, QCoin value, claim requirements, and reward availability. GatiQ Rewards provides the platform to manage and follow that programme.",
      contact: "Discuss your programme",
      login: "Platform login",
    },
    help: {
      title: "Questions about a specific reward?",
      text: "Your company or local counter can confirm which products qualify and explain the rules for your programme. For general help, contact the GatiQ Rewards team.",
      call: "Call +91 93511 66339",
      whatsapp: "WhatsApp us",
      email: "Email info@gatiq.in",
    },
  },
  hi: {
    karigars: {
      intro: "इनाम का सफ़र, आसान कदमों में",
      introText: "आप हुनर का काम करते हैं। GatiQ Rewards आपको योग्य प्रोडक्ट का इनाम कोड पहचानने और आगे के कदम समझने में मदद करता है।",
      steps: [
        { title: "कोड देखें", description: "अपनी कंपनी के कार्यक्रम में शामिल प्रोडक्ट पर GatiQ Rewards QR कोड देखें।" },
        { title: "स्कैन करें या नंबर डालें", description: "ऐप से QR कोड स्कैन करें। स्कैन न हो तो नीचे छपा नंबर डालें।" },
        { title: "QCoin देखें", description: "कोड सही होने पर QCoin की एंट्री ऐप में दिखती है। अपना बैलेंस और गतिविधि भी देख सकते हैं।" },
        { title: "क्लेम के कदम पूरे करें", description: "रजिस्ट्रेशन मंज़ूर होने के बाद ऐप में क्लेम रिक्वेस्ट भेजें और कार्यक्रम के निर्देश मानें।" },
      ],
      noteTitle: "हर कंपनी के कार्यक्रम के नियम अलग हो सकते हैं",
      note: "कौन-से प्रोडक्ट योग्य हैं, कोड से कितने QCoin मिलेंगे और कौन-से इनाम मिल सकते हैं—यह कंपनी तय करती है। अपने कार्यक्रम की जानकारी कंपनी प्रतिनिधि या काउंटर से लें।",
      screenTitle: "ऐप में आपका इनाम सफ़र",
      screenAlt: "कारीगर के इनाम सफ़र को दिखाती GatiQ Rewards ऐप स्क्रीन",
      help: "मदद चाहिए? सहायता पेज देखें",
      contact: "हमारी टीम से बात करें",
    },
    companies: {
      intro: "कार्यक्रम चलाने और देखने के साधन",
      introText: "कोड बैच, नेटवर्क गतिविधि और प्रोडक्ट जानकारी को एक साझा जगह से संभालें।",
      capabilities: [
        { title: "कोड बैच जारी करें", description: "अपने कार्यक्रम के प्रोडक्ट के लिए इनाम कोड बैच बनाएँ।" },
        { title: "अपना नेटवर्क जोड़ें", description: "डिस्ट्रीब्यूटर, काउंटर और कारीगरों को कार्यक्रम से जोड़ें।" },
        { title: "स्कैन और बैच देखें", description: "जारी होने से स्कैन गतिविधि तक बैच का रिकॉर्ड देखें।" },
        { title: "प्रदर्शन देखें और जानकारी बाँटें", description: "प्रोडक्ट और काउंटर का प्रदर्शन देखें और नेटवर्क के साथ प्रोडक्ट जानकारी साझा करें।" },
      ],
      noteTitle: "कार्यक्रम के नियम आप तय करते हैं",
      note: "योग्य प्रोडक्ट, QCoin की कीमत, क्लेम की ज़रूरतें और उपलब्ध इनाम हर कंपनी तय करती है। GatiQ Rewards उस कार्यक्रम को संभालने और देखने का प्लेटफ़ॉर्म देता है।",
      contact: "अपने कार्यक्रम पर बात करें",
      login: "प्लेटफ़ॉर्म लॉगिन",
    },
    help: {
      title: "किसी खास इनाम के बारे में सवाल है?",
      text: "आपकी कंपनी या नज़दीकी काउंटर बता सकता है कि कौन-से प्रोडक्ट योग्य हैं और आपके कार्यक्रम के नियम क्या हैं। सामान्य मदद के लिए GatiQ Rewards टीम से बात करें।",
      call: "+91 93511 66339 पर कॉल करें",
      whatsapp: "WhatsApp करें",
      email: "info@gatiq.in पर ईमेल करें",
    },
  },
} as const;

function KarigarsContent({ t, lang }: { t: LandingCopy; lang: "en" | "hi" }) {
  const c = audienceCopy[lang].karigars;
  return (
    <>
      <section className="gq-lp-section gq-lp-audience-detail">
        <div className="gq-lp-wrap">
          <div className="gq-lp-section-intro is-centered">
            <p className="gq-lp-eyebrow is-orange">{lang === "en" ? "Made for your day-to-day work" : "आपके रोज़ के काम के लिए"}</p>
            <h2>{c.intro}</h2><p>{c.introText}</p>
          </div>
          <div className="gq-lp-journey-grid gq-lp-karigar-steps">
            {c.steps.map((step, index) => {
              const StepIcon = journeyIcons[index]!;
              return <article className="gq-lp-journey-card" key={step.title}>
                <span className="gq-lp-journey-number">{String(index + 1).padStart(2, "0")}</span>
                <span className={`gq-lp-journey-icon ${["is-orange", "is-green", "is-sand", "is-rose"][index]}`}><StepIcon size={21} /></span>
                <h3>{step.title}</h3><p>{step.description}</p>
              </article>;
            })}
          </div>
        </div>
      </section>
      <DemoScreensShowcase
        lang={lang}
        eyebrow={lang === "en" ? "ZENITH PRO ADHESIVES · KARIGAR APP" : "ZENITH PRO ADHESIVES · कारीगर ऐप"}
        title={lang === "en" ? "From scan to claim, on mobile" : "मोबाइल पर स्कैन से क्लेम तक"}
        description={lang === "en" ? "See the Karigar app screens for the balance, product scan, claim request, and shared product information." : "बैलेंस, प्रोडक्ट स्कैन, क्लेम अनुरोध और साझा प्रोडक्ट जानकारी के लिए कारीगर ऐप की स्क्रीन देखें।"}
        screens={[
          { image: "zenith-karigar-home.png", title: lang === "en" ? "QCoin balance" : "QCoin बैलेंस", alt: "Zenith Club Karigar app home screen with QCoin balance", phone: true },
          { image: "zenith-karigar-scan.png", title: lang === "en" ? "Scan a product code · sample QR" : "प्रोडक्ट कोड स्कैन · नमूना QR", alt: "Zenith Club Karigar app scan screen with illustrative sample QR animation", phone: true, scan: true },
          { image: "zenith-karigar-redemption.png", title: lang === "en" ? "Request a claim" : "क्लेम अनुरोध भेजें", alt: "Zenith Club Karigar app QCoin claim request screen", phone: true },
          { image: "zenith-karigar-documents.png", title: lang === "en" ? "Product information" : "प्रोडक्ट जानकारी", alt: "Zenith Club Karigar app product documents and catalogue list", phone: true },
        ]}
      />
      <section className="gq-lp-section gq-lp-karigar-app">
        <div className="gq-lp-wrap gq-lp-karigar-app-grid">
          <figure className="gq-lp-feature-screen">
            <div className="gq-lp-screen-topline"><span />GatiQ Rewards <small>{lang === "en" ? "KARIGAR APP" : "कारीगर ऐप"}</small></div>
            <img src={`${ASSET_ROOT}/zenith-karigar-home.png`} alt="Zenith Club Karigar app home showing its QCoin balance and scan action" />
            <figcaption><span>{lang === "en" ? "App screen" : "ऐप स्क्रीन"}</span><span>{c.screenTitle}</span></figcaption>
          </figure>
          <div className="gq-lp-program-note">
            <p className="gq-lp-eyebrow is-orange">{lang === "en" ? "Good to know" : "ज़रूरी जानकारी"}</p>
            <h2>{c.noteTitle}</h2><p>{c.note}</p>
            <Link className="gq-lp-text-link" to="/help">{c.help} <ArrowRight size={15} /></Link>
          </div>
        </div>
      </section>
      <AudienceCta lang={lang} primary={c.contact} secondary={c.help} />
    </>
  );
}

function CompaniesContent({ t, lang, activeFeature, setActiveFeature }: { t: LandingCopy; lang: "en" | "hi"; activeFeature: number; setActiveFeature: (index: number) => void }) {
  const c = audienceCopy[lang].companies;
  return (
    <>
      <section className="gq-lp-section gq-lp-audience-detail">
        <div className="gq-lp-wrap">
          <div className="gq-lp-section-intro is-centered"><p className="gq-lp-eyebrow is-orange">{lang === "en" ? "The programme, clearly managed" : "कार्यक्रम का साफ़ प्रबंधन"}</p><h2>{c.intro}</h2><p>{c.introText}</p></div>
          <div className="gq-lp-company-capabilities">
            {c.capabilities.map((item, index) => <article className="gq-lp-company-capability" key={item.title}><span>{String(index + 1).padStart(2, "0")}</span><h3>{item.title}</h3><p>{item.description}</p></article>)}
          </div>
        </div>
      </section>
      <DemoScreensShowcase
        lang={lang}
        eyebrow={lang === "en" ? "ONE NETWORK · EVERY SCREEN" : "एक नेटवर्क · हर स्क्रीन"}
        title={lang === "en" ? "Admin, counter, and Karigar views" : "Admin, काउंटर और कारीगर के स्क्रीन"}
        description={lang === "en" ? "Explore the actual Zenith Pro Adhesives workspace across desktop, tablet, and mobile." : "डेस्कटॉप, टैबलेट और मोबाइल पर Zenith Pro Adhesives के वास्तविक वर्कस्पेस स्क्रीन देखें।"}
        screens={[
          { image: "zenith-admin-overview.png", title: lang === "en" ? "Desktop · Admin dashboard" : "डेस्कटॉप · Admin डैशबोर्ड", alt: "Zenith Pro Adhesives desktop Admin overview dashboard" },
          { image: "zenith-performance-tablet.png", title: lang === "en" ? "Tablet · Performance tracker" : "टैबलेट · परफॉर्मेंस ट्रैकर", alt: "Zenith Pro Adhesives performance tracker on tablet" },
          { image: "zenith-counter-dashboard.png", title: lang === "en" ? "Counter dashboard" : "काउंटर डैशबोर्ड", alt: "Zenith Pro Adhesives Counter dashboard" },
          { image: "zenith-karigar-home.png", title: lang === "en" ? "Mobile · Karigar app" : "मोबाइल · कारीगर ऐप", alt: "Zenith Club Karigar app home screen", phone: true },
        ]}
        connected
      />
      <PlatformContent t={t} lang={lang} activeFeature={activeFeature} setActiveFeature={setActiveFeature} />
      <JourneyContent t={t} lang={lang} />
      <section className="gq-lp-section gq-lp-company-note"><div className="gq-lp-wrap gq-lp-program-note"><p className="gq-lp-eyebrow is-orange">{lang === "en" ? "Programme design" : "कार्यक्रम के नियम"}</p><h2>{c.noteTitle}</h2><p>{c.note}</p></div></section>
      <AudienceCta lang={lang} primary={c.contact} secondary={c.login} company />
    </>
  );
}

function HelpContent({ t, lang }: { t: LandingCopy; lang: "en" | "hi" }) {
  const c = audienceCopy[lang].help;
  return (
    <>
      <section className="gq-lp-section gq-lp-help-content">
        <div className="gq-lp-wrap gq-lp-faq-grid">
          <div className="gq-lp-faq-heading"><p className="gq-lp-eyebrow is-orange">{t.faq.eyebrow}</p><h2>{t.faq.title}</h2><p>{t.faq.intro}</p></div>
          <div className="gq-lp-faq-list">{t.faq.items.map((item) => <details key={item.question}><summary>{item.question}<span aria-hidden="true">⌄</span></summary><p>{item.answer}</p></details>)}</div>
        </div>
      </section>
      <DemoScreensShowcase
        lang={lang}
        eyebrow={lang === "en" ? "QUICK VISUAL GUIDE" : "त्वरित दृश्य मार्गदर्शिका"}
        title={lang === "en" ? "Find the next step in the app" : "ऐप में अगला कदम देखें"}
        description={lang === "en" ? "The Zenith Pro Adhesives demo shows where to scan, request a claim, and open shared product information." : "Zenith Pro Adhesives डेमो में स्कैन, क्लेम अनुरोध और साझा प्रोडक्ट जानकारी के स्क्रीन देखें।"}
        screens={[
          { image: "zenith-karigar-scan.png", title: lang === "en" ? "Scan a product code · sample QR" : "प्रोडक्ट कोड स्कैन · नमूना QR", alt: "Zenith Club Karigar scan screen with illustrative QR", phone: true, scan: true },
          { image: "zenith-karigar-redemption.png", title: lang === "en" ? "Request a claim" : "क्लेम अनुरोध भेजें", alt: "Zenith Club Karigar app claim request screen", phone: true },
          { image: "zenith-karigar-documents.png", title: lang === "en" ? "Open product information" : "प्रोडक्ट जानकारी खोलें", alt: "Zenith Club Karigar app product documents list", phone: true },
        ]}
      />
      <section className="gq-lp-section gq-lp-support-section"><div className="gq-lp-wrap gq-lp-support-card"><div><p className="gq-lp-eyebrow is-orange">{lang === "en" ? "We’re here to help" : "हम मदद के लिए हैं"}</p><h2>{c.title}</h2><p>{c.text}</p></div><div className="gq-lp-support-links">
        <a href="tel:+919351166339"><Phone size={16} aria-hidden="true" />{c.call}<ArrowUpRight size={15} aria-hidden="true" /></a>
        <a href="https://wa.me/919351166339" target="_blank" rel="noreferrer"><MessageCircle size={16} aria-hidden="true" />{c.whatsapp}<ArrowUpRight size={15} aria-hidden="true" /></a>
        <a href="mailto:info@gatiq.in"><Mail size={16} aria-hidden="true" />{c.email}<ArrowUpRight size={15} aria-hidden="true" /></a>
      </div></div></section>
    </>
  );
}

function AudienceCta({ lang, primary, secondary, company = false }: { lang: "en" | "hi"; primary: string; secondary: string; company?: boolean }) {
  return <section className="gq-lp-audience-cta"><div className="gq-lp-wrap"><div><p className="gq-lp-eyebrow">{lang === "en" ? "Take the next step" : "अगला कदम लें"}</p><h2>{lang === "en" ? "Let’s make the reward journey clearer." : "इनाम के सफ़र को और आसान बनाएँ।"}</h2></div><div className="gq-lp-actions">{company ? <Link className="gq-lp-button gq-lp-button-primary" to="/contact">{primary}<ArrowUpRight size={15} /></Link> : <a className="gq-lp-button gq-lp-button-primary" href="tel:+919351166339">{primary}<ArrowUpRight size={15} /></a>}<Link className="gq-lp-button gq-lp-button-quiet" to={company ? "/login" : "/help"}>{secondary}<ArrowRight size={15} /></Link></div></div></section>;
}

function JourneyContent({ t, lang }: { t: LandingCopy; lang: LandingLanguage }) {
  return (
    <>
      <section className="gq-lp-section gq-lp-network">
        <div className="gq-lp-wrap">
          <div className="gq-lp-section-intro is-centered">
            <p className="gq-lp-eyebrow is-orange">{t.network.eyebrow}</p>
            <h2>{t.network.titleFirst}<br /><span>{t.network.titleSecond}</span></h2>
            <p>{t.network.description}</p>
          </div>
          <div className="gq-lp-network-flow" aria-label={t.network.aria}>
            {t.network.roles.map((role, index) => {
              const RoleIcon = roleIcons[index]!;
              return (
                <div className="gq-lp-network-step" key={role.title}>
                  <article className="gq-lp-network-node">
                    <span className={`gq-lp-node-mark ${["is-orange", "is-sand", "is-green", "is-rose"][index]}`}><RoleIcon size={18} /></span>
                    <div><small>{role.meta}</small><h3>{role.title}</h3><p>{role.description}</p></div>
                  </article>
                  {index < t.network.roles.length - 1 ? <span className="gq-lp-network-link" aria-hidden="true"><ArrowRight size={15} /></span> : null}
                </div>
              );
            })}
          </div>
          <p className="gq-lp-network-note"><Check size={14} />{t.network.note}</p>
        </div>
      </section>
      <section className="gq-lp-section gq-lp-journey">
        <div className="gq-lp-wrap">
          <div className="gq-lp-section-heading-row">
            <div className="gq-lp-section-intro">
              <p className="gq-lp-eyebrow is-orange">{t.journey.eyebrow}</p>
              <h2>{t.journey.titleFirst}<br /><span>{t.journey.titleSecond}</span></h2>
            </div>
            <p className="gq-lp-section-side-note">{t.journey.sideNote}</p>
          </div>
          <div className="gq-lp-journey-grid">
            {t.journey.steps.map((step, index) => {
              const StepIcon = journeyIcons[index]!;
              return (
                <article className="gq-lp-journey-card" key={step.tag}>
                  <span className="gq-lp-journey-number">{String(index + 1).padStart(2, "0")}</span>
                  <span className={`gq-lp-journey-icon ${["is-orange", "is-green", "is-sand", "is-rose"][index]}`}><StepIcon size={21} /></span>
                  <h3>{step.title}</h3><p>{step.description}</p>
                  <span className="gq-lp-journey-tag">{step.tag}</span>
                </article>
              );
            })}
          </div>
        </div>
      </section>
      <DemoScreensShowcase
        lang={lang}
        eyebrow={lang === "en" ? "FROM CODE TO CLAIM" : "कोड से क्लेम तक"}
        title={lang === "en" ? "Follow the journey on screen" : "स्क्रीन पर रिवॉर्ड यात्रा देखें"}
        description={lang === "en" ? "The product batch, Karigar scan, and claim request all connect to the same reward programme." : "प्रोडक्ट बैच, कारीगर स्कैन और क्लेम अनुरोध एक ही रिवॉर्ड प्रोग्राम से जुड़े हैं।"}
        screens={[
          { image: "zenith-reward-batches.png", title: lang === "en" ? "Company · Reward batch" : "कंपनी · रिवॉर्ड बैच", alt: "Zenith Pro Adhesives reward batch tracking screen" },
          { image: "zenith-karigar-scan.png", title: lang === "en" ? "Karigar · Scan · sample QR" : "कारीगर · स्कैन · नमूना QR", alt: "Zenith Club Karigar scan screen with illustrative QR", phone: true, scan: true },
          { image: "zenith-karigar-redemption.png", title: lang === "en" ? "Karigar · Request a claim" : "कारीगर · क्लेम अनुरोध", alt: "Zenith Club Karigar claim request screen", phone: true },
        ]}
      />
    </>
  );
}

function PlatformContent({
  t,
  lang,
  activeFeature,
  setActiveFeature,
}: {
  t: LandingCopy;
  lang: LandingLanguage;
  activeFeature: number;
  setActiveFeature: (index: number) => void;
}) {
  const features = platformScreenInfo[lang];
  const feature = features[activeFeature]!;
  return (
    <section className="gq-lp-section gq-lp-platform">
      <div className="gq-lp-wrap gq-lp-platform-grid">
        <div className="gq-lp-platform-copy">
          <p className="gq-lp-eyebrow is-orange">{t.platform.eyebrow}</p>
          <h2>{t.platform.titleFirst}<br /><span>{t.platform.titleSecond}</span></h2>
          <p>{t.platform.description}</p>
          <div className="gq-lp-feature-tabs" role="group" aria-label={t.platform.featureGroup}>
            {features.map((item, index) => (
              <button
                className={index === activeFeature ? "gq-lp-feature-tab is-active" : "gq-lp-feature-tab"}
                type="button"
                key={item.title}
                aria-pressed={index === activeFeature}
                onClick={() => setActiveFeature(index)}
              >
                <span>{String(index + 1).padStart(2, "0")}</span>{item.title}
              </button>
            ))}
          </div>
          <p className="gq-lp-feature-description" aria-live="polite">{feature.description}</p>
        </div>
        <figure className="gq-lp-feature-screen">
          <div className="gq-lp-screen-topline"><span />GatiQ Rewards <small>{t.platform.screenLabel}</small></div>
          <img key={featureImages[activeFeature]} src={`${ASSET_ROOT}/${featureImages[activeFeature]}`} alt={feature.alt} />
          <figcaption><span>{t.platform.actualUi}</span><span>{feature.title}</span></figcaption>
        </figure>
      </div>
    </section>
  );
}

function FilmContent({ t }: { t: LandingCopy }) {
  return (
    <section className="gq-lp-section gq-lp-film">
      <div className="gq-lp-wrap gq-lp-film-grid">
        <div className="gq-lp-film-copy">
          <p className="gq-lp-eyebrow is-orange">{t.film.eyebrow}</p>
          <h2>{t.film.titleFirst}<br /><span>{t.film.titleSecond}</span></h2>
          <p>{t.film.description}</p>
          <div className="gq-lp-film-meta"><span><Play size={14} aria-hidden="true" /></span><span><b>{t.film.title}</b><small>{t.film.detail}</small></span></div>
        </div>
        <div className="gq-lp-film-frame">
          <video controls playsInline preload="metadata" poster={`${ASSET_ROOT}/zenith-admin-overview.png`} aria-label={t.film.videoAlt}>
            <source src={`${ASSET_ROOT}/gatiq-rewards-product-film.mp4`} type="video/mp4" />
          </video>
          <p>{t.film.disclosure}</p>
        </div>
      </div>
    </section>
  );
}

function LegalContent({ sections }: { sections: readonly { title: string; body: string }[] }) {
  return (
    <section className="gq-lp-section gq-lp-legal">
      <div className="gq-lp-wrap gq-lp-legal-list">
        {sections.map((section, index) => (
          <article className="gq-lp-legal-card" key={section.title}>
            <span>{String(index + 1).padStart(2, "0")}</span>
            <div><h2>{section.title}</h2><p>{section.body}</p></div>
          </article>
        ))}
        <p className="gq-lp-legal-contact"><a href="mailto:info@gatiq.in">info@gatiq.in</a></p>
      </div>
    </section>
  );
}

function ContactContent({
  copy,
  privacyLabel,
}: {
  copy: SiteCopy["contact"];
  privacyLabel: string;
}) {
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<"idle" | "success" | "error">("idle");

  async function submitContact(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    const payload = Object.fromEntries(data.entries());
    setSubmitting(true);
    setResult("idle");
    try {
      const response = await fetch("/api/public/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error("Contact request failed");
      form.reset();
      setResult("success");
    } catch {
      setResult("error");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section className="gq-lp-section gq-lp-contact">
      <div className="gq-lp-wrap gq-lp-contact-grid">
        <div className="gq-lp-contact-aside">
          <p className="gq-lp-eyebrow is-orange">{copy.direct}</p>
          <h2>{copy.heading}</h2>
          <p>{copy.intro}</p>
          <a href="mailto:info@gatiq.in">info@gatiq.in <ArrowUpRight size={15} /></a>
          <a href="tel:+919351166339">+91 93511 66339 <ArrowUpRight size={15} /></a>
          <a href="https://wa.me/919351166339" target="_blank" rel="noreferrer">{copy.whatsapp} <ArrowUpRight size={15} /></a>
        </div>
        <form className="gq-lp-contact-form" onSubmit={submitContact}>
          <div className="gq-lp-form-row">
            <label>{copy.name}<input autoComplete="name" maxLength={100} name="name" placeholder={copy.namePlaceholder} required /></label>
            <label>{copy.email}<input autoComplete="email" maxLength={254} name="email" placeholder={copy.emailPlaceholder} required type="email" /></label>
          </div>
          <div className="gq-lp-form-row">
            <label>{copy.phone}<input autoComplete="tel" maxLength={30} name="phone" placeholder={copy.phonePlaceholder} /></label>
            <label>{copy.subject}<input maxLength={120} name="subject" placeholder={copy.subjectPlaceholder} required /></label>
          </div>
          <label>{copy.message}<textarea maxLength={5000} minLength={10} name="message" placeholder={copy.messagePlaceholder} required rows={6} /></label>
          <button className="gq-lp-button gq-lp-button-primary" disabled={submitting} type="submit">
            {submitting ? copy.sending : copy.send}<ArrowUpRight size={15} />
          </button>
          {result === "success" ? <p className="gq-lp-form-result is-success" role="status">{copy.success}</p> : null}
          {result === "error" ? <p className="gq-lp-form-result is-error" role="alert">{copy.failure}</p> : null}
          <p className="gq-lp-form-privacy">{copy.privacy} <a href="/privacy">{privacyLabel}</a></p>
        </form>
      </div>
    </section>
  );
}
